/* eslint-disable react-refresh/only-export-components */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { OrderHubOverviewTab } from '@/app/(client)/orders-hub/-components/OrderHubOverviewTab';
import {
  referencedCompaniesQueryOptions,
  useOrderHubCompanyOptions,
} from '@/app/(client)/orders-hub/-hooks/useOrderHubCompanyOptions';
import { useOrderHubCounterpartyProfile } from '@/app/(client)/orders-hub/-hooks/useOrderHubCounterpartyProfile';
import { useOrderHubLinesTable } from '@/app/(client)/orders-hub/-hooks/useOrderHubLinesTable';
import {
  orderHubDetailQueryOptions,
  useOrderHubOrderDetail,
} from '@/app/(client)/orders-hub/-hooks/useOrderHubOrderDetail';
import { orderHubOrderLinesQueryOptions } from '@/app/(client)/orders-hub/-hooks/useOrderHubOrderLinesList';
import { useOrderHubOverview } from '@/app/(client)/orders-hub/-hooks/useOrderHubOverview';
import { useUpdateOrderHubOrder } from '@/app/(client)/orders-hub/-hooks/useUpdateOrderHubOrder';
import { queryClient } from '@/lib/utils/queryClient';

interface OverviewSearch {
  /** Present only when the Order Lines rail node is selected. */
  section?: 'lines';
}

export const Route = createFileRoute(
  '/(client)/orders-hub/(pages)/(detail)/$id/overview',
)({
  validateSearch: (search: Record<string, unknown>): OverviewSearch =>
    search.section === 'lines' ? { section: 'lines' } : {},
  loader: async ({ params }) => {
    const [order] = await Promise.all([
      queryClient.ensureQueryData(orderHubDetailQueryOptions(params.id)),
      queryClient.ensureQueryData(orderHubOrderLinesQueryOptions(params.id)),
    ]);
    // Warm only the ≤4 referenced companies (id-scoped) so their names paint
    // without a flash — no eager full-network fetch on open.
    const ids = [
      order.buyerCompanyId,
      order.supplierCompanyId,
      order.receiverCompanyId,
      order.vendorCompanyId,
    ].filter((id): id is string => !!id);
    if (ids.length > 0) {
      await queryClient.ensureQueryData(referencedCompaniesQueryOptions(ids));
    }
  },
  component: OrderHubOverviewRoute,
});

function OrderHubOverviewRoute() {
  const { id } = Route.useParams();
  const { section } = Route.useSearch();
  const navigate = useNavigate();

  const [picklistEnabled, setPicklistEnabled] = useState(false);

  const { data: order } = useOrderHubOrderDetail(id);
  const { options, isPicklistLoading } = useOrderHubCompanyOptions(order, {
    picklistEnabled,
  });
  const { patch } = useUpdateOrderHubOrder(id);
  const supplierProfile = useOrderHubCounterpartyProfile(
    order?.supplierCompanyId,
  );
  const buyerProfile = useOrderHubCounterpartyProfile(order?.buyerCompanyId);
  const linesTable = useOrderHubLinesTable(id);

  const overview = useOrderHubOverview({
    order,
    companyOptions: options,
    onPatch: patch,
    supplierProfile,
    buyerProfile,
    linesCount: linesTable.lines.length,
    activeSection: section === 'lines' ? 'lines' : 'info',
    selectSection: next =>
      void navigate({
        to: '/orders-hub/$id/overview',
        params: { id },
        search: next === 'lines' ? { section: 'lines' } : {},
      }),
    onCompanyEditOpen: () => setPicklistEnabled(true),
    picklistLoading: isPicklistLoading,
  });

  if (!order) return null;

  return <OrderHubOverviewTab overview={overview} linesTable={linesTable} />;
}
